import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, User, LogIn, Radio } from "lucide-react";
import { NAV_LINKS } from "../lib/constants";
import { BuflLogo } from "./Logo";
import { useFanAuth } from "../context/FanAuthContext";
import { fetchJson } from "../lib/api";

// -----------------------------------------------------------------------------
// Internal sub-components extracted from Header so the top-level JSX stays
// scannable. Purely presentational — no new behaviour, no new state.
// -----------------------------------------------------------------------------
function DesktopNavLinks({ isLive }) {
  return (
    <nav className="hidden lg:flex items-center gap-1">
      {NAV_LINKS.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.to === "/"}
          data-testid={link.testId}
          className={({ isActive }) => {
            const isLiveLink = link.to === "/live";
            return `px-3 py-2 text-xs uppercase tracking-[0.18em] font-display font-bold transition-colors duration-200 ${
              isActive
                ? "text-white border-b border-gold"
                : "text-neutral-400 hover:text-white"
            } ${isLiveLink && isLive ? "!text-burkina-red" : ""}`;
          }}
        >
          <span className="inline-flex items-center gap-1">
            {link.to === "/live" && isLive && <span className="w-1.5 h-1.5 rounded-full bg-burkina-red animate-pulse" />}
            {link.label}
          </span>
        </NavLink>
      ))}
    </nav>
  );
}

function DesktopAccountArea({ user }) {
  if (user) {
    return (
      <div className="hidden lg:flex items-center gap-2">
        <Link
          to="/mon-compte"
          data-testid="header-account-link"
          className="inline-flex items-center gap-2 px-3 h-9 border border-neutral-800 text-neutral-300 font-display uppercase tracking-widest text-[11px] hover:text-white hover:border-neutral-700 transition-colors"
        >
          <User size={12} /> {(user.full_name || user.email || "").split("@")[0].slice(0, 14)}
        </Link>
      </div>
    );
  }
  return (
    <div className="hidden lg:flex items-center gap-2">
      <Link
        to="/login"
        data-testid="header-login-link"
        className="inline-flex items-center gap-2 px-3 h-9 border border-neutral-800 text-neutral-300 font-display uppercase tracking-widest text-[11px] hover:text-white hover:border-neutral-700 transition-colors"
      >
        <LogIn size={12} /> Connexion
      </Link>
    </div>
  );
}

function MobileNavPanel({ isLive, user, onClose }) {
  return (
    <div className="lg:hidden border-t border-neutral-900 bg-black" data-testid="mobile-menu-panel">
      <nav className="flex flex-col">
        {NAV_LINKS.map((link) => {
          const isLiveLink = link.to === "/live";
          return (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`mobile-${link.testId}`}
              onClick={onClose}
              className={({ isActive }) =>
                `px-6 py-4 text-sm uppercase tracking-[0.2em] font-display font-bold border-b border-neutral-900 transition-colors duration-200 ${
                  isActive ? "text-white bg-neutral-950" : "text-neutral-400"
                } ${isLiveLink && isLive ? "!text-burkina-red" : ""}`
              }
            >
              <span className="inline-flex items-center gap-2">
                {isLiveLink && isLive && <span className="w-2 h-2 rounded-full bg-burkina-red animate-pulse" />}
                {link.label}
              </span>
            </NavLink>
          );
        })}
        {user ? (
          <Link
            to="/mon-compte"
            onClick={onClose}
            data-testid="mobile-account-link"
            className="px-6 py-4 text-sm uppercase tracking-[0.2em] font-display font-bold border-b border-neutral-900 text-gold"
          >
            Mon compte
          </Link>
        ) : (
          <>
            <Link
              to="/login"
              onClick={onClose}
              data-testid="mobile-login-link"
              className="px-6 py-4 text-sm uppercase tracking-[0.2em] font-display font-bold border-b border-neutral-900 text-neutral-300"
            >
              Se connecter
            </Link>
            <Link
              to="/register"
              onClick={onClose}
              data-testid="mobile-register-link"
              className="px-6 py-4 text-sm uppercase tracking-[0.2em] font-display font-bold border-b border-neutral-900 text-gold"
            >
              Créer un compte
            </Link>
          </>
        )}
      </nav>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const { user } = useFanAuth();

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const d = await fetchJson("/live/current");
        if (alive) setIsLive(d?.state === "live");
      } catch (_e) {
        if (alive) setIsLive(false);
      }
    };
    check();
    const t = setInterval(check, 60000); // re-check every minute
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-black/85 backdrop-blur-xl transition-colors ${
        isLive ? "border-burkina-red" : "border-neutral-900"
      }`}
      data-testid="site-header"
    >
      {/* Fight Night ribbon */}
      {isLive && (
        <Link
          to="/live"
          className="block bg-burkina-red text-white text-center py-1.5 text-[11px] font-display uppercase tracking-widest hover:bg-burkina-red/90"
          data-testid="fight-night-ribbon"
        >
          <span className="inline-flex items-center gap-2 animate-pulse">
            <Radio size={11} /> BUFL LIVE — Regarder en direct maintenant →
          </span>
        </Link>
      )}
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        <div className="flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="header-logo-link">
            <BuflLogo />
          </Link>

          <DesktopNavLinks isLive={isLive} />
          <DesktopAccountArea user={user} />

          <button
            className="lg:hidden text-white p-2 -mr-2"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <MobileNavPanel isLive={isLive} user={user} onClose={() => setOpen(false)} />
      )}
    </header>
  );
}
